import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createFromTemplate } from '../../services/templateService';

// 默认占位图 - 使用 SVG Data URI
const DEFAULT_PREVIEW_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect fill='%23f5f5f5' width='800' height='600'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' dy='10.5' font-weight='bold' x='50%25' y='50%25' text-anchor='middle'%3E模板預覽圖%3C/text%3E%3C/svg%3E";

const TemplateDetailDialog = ({ open, onClose, template, onActivityCreated }) => {
  const { t } = useTranslation('homepage');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const userId = localStorage.getItem('userId');

  if (!template) {
    return null;
  }

  const handleCreate = async () => {
    if (!template || !userId) {
      setError(t('templateDetail.missingInfo'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createFromTemplate(template.id, userId);
      
      // 創建成功，通知父組件刷新活動列表（同時設置 localStorage 和 sessionStorage）
      if (result.activity && result.activity.id) {
        localStorage.setItem('activityId', result.activity.id);
        sessionStorage.setItem('activityId', result.activity.id);
        if (result.group && result.group.id) {
          localStorage.setItem('groupId', result.group.id);
          sessionStorage.setItem('groupId', result.group.id);
        }
        
        // 調用回調函數通知父組件
        if (onActivityCreated) {
          onActivityCreated(result);
        }
        
        // 關閉對話框
        onClose();
      }
    } catch (err) {
      setError(err.message || t('templateDetail.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      return '';
    }
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getChatModeLabel = (chatmode) => {
    const labels = {
      'sticker': t('templateDetail.chatMode.sticker'),
      'chatroom': t('templateDetail.chatMode.chatroom'),
      'tagged-forum': t('templateDetail.chatMode.taggedForum')
    };
    return labels[chatmode] || chatmode;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{template.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" paragraph>
          {template.description}
        </Typography>

        {(template.previewImage || imageError) && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <img
              src={imageError ? DEFAULT_PREVIEW_IMAGE : (template.previewImage || DEFAULT_PREVIEW_IMAGE)}
              alt={template.name}
              onError={() => setImageError(true)}
              style={{ 
                maxWidth: '400px',
                maxHeight: '300px',
                width: 'auto',
                height: 'auto', 
                borderRadius: '8px',
                backgroundColor: '#f5f5f5'
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('templateDetail.activitySettings')}
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('templateDetail.titleLabel')}</strong>{template.activity?.title || t('templateDetail.notSet')}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              <strong>開始日期：</strong>
              {template.activity?.startDate ? formatDate(template.activity.startDate) : '立即開始'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>結束日期：</strong>
              {template.activity?.endDate ? formatDate(template.activity.endDate) : '未設定'}
            </Typography> */}
            <Typography variant="body2" color="text.secondary">
              <strong>{t('templateDetail.modeLabel')}</strong>
              {template.activity?.settings?.chatmode 
                ? getChatModeLabel(template.activity.settings.chatmode)
                : t('templateDetail.notSet')}
            </Typography>
          </Box>
        </Box>

        {template.partners && template.partners.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('templateDetail.aiPartners')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 2 }}>
              {template.partners.map((partner, index) => (
                <Chip
                  key={index}
                  label={partner.partnerName || partner.name || t('templateDetail.partnerDefault', { index: index + 1 })}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('templateDetail.cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('templateDetail.creating') : t('templateDetail.confirmCreate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateDetailDialog;

